// Pure helpers for the premium attendee form (kept separate so they can be unit tested).

export const DEFAULT_SECTION = 'Your details';

/**
 * The event form as the premium page shows it: the layout (by label) sets order, section, pass list and type,
 * hides fields and adds premium-only ones; unlisted form fields follow. Mirrors BookingService.premiumFields.
 */
export const applyLayout = (schema = [], layout = []) => {
  if (!layout?.length) return schema;
  const byLabel = new Map();
  schema.forEach((field) => field?.label && !byLabel.has(field.label.toLowerCase()) && byLabel.set(field.label.toLowerCase(), field));
  const placed = new Set();
  const out = [];
  layout.forEach((entry) => {
    const key = entry.label.toLowerCase();
    placed.add(key);
    if (entry.hidden) return;
    const field = { ...(byLabel.get(key) || { label: entry.label, type: 'text' }) };
    if (entry.type) field.type = entry.type;
    if (entry.required !== null && entry.required !== undefined) field.required = entry.required;
    if (entry.section) field.section = entry.section;
    if (entry.passIds?.length) field.passIds = entry.passIds;
    if (entry.options?.length) field.options = entry.options;
    if (entry.placeholder) field.placeholder = entry.placeholder;
    if (entry.width || entry.height) field.validation = { ...(field.validation || {}), width: entry.width, height: entry.height };
    out.push(field);
  });
  schema.forEach((field) => { if (!field?.label || !placed.has(field.label.toLowerCase())) out.push(field); });
  return out;
};

/** Fields this pass asks for: ones without a pass list, or listing the pass. */
export const fieldsForPass = (schema = [], passId, layout = []) => applyLayout(schema, layout)
  .filter((field) => field && field.label && (!field.passIds?.length || field.passIds.includes(passId)))
  .map((field, i) => ({ ...field, id: `tk-field-${i}` }));

/** Fields grouped into sections in first-appearance order; fields without a section join the first one. */
export const groupSections = (fields) => {
  const sections = [];
  const byName = new Map();
  const first = fields.find((f) => f.section)?.section || DEFAULT_SECTION;
  fields.forEach((field) => {
    const name = field.section || first;
    if (!byName.has(name)) {
      byName.set(name, { title: name, fields: [] });
      sections.push(byName.get(name));
    }
    byName.get(name).fields.push(field);
  });
  return sections;
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const isBlank = (value) => value === undefined || value === null || value === false
  || (typeof value === 'string' && !value.trim()) || (Array.isArray(value) && value.length === 0);

export const emailFieldOf = (fields) => fields.find((f) => f.type === 'email') || fields.find((f) => /^e-?mail/i.test(f.label));

/** Error message per field id for one attendee's answers. */
export const validateFields = (fields, values, { verifyEmail = false, tokens = {} } = {}) => {
  const errors = {};
  fields.forEach((field) => {
    const value = values[field.label];
    if (field.required && isBlank(value)) {
      errors[field.id] = field.type === 'consent' ? 'Please accept to continue' : 'This field is required';
      return;
    }
    if (isBlank(value)) return;
    if (field.type === 'email' && !EMAIL.test(String(value).trim())) {
      errors[field.id] = 'Enter a valid email address';
    } else if (field.type === 'email' && verifyEmail && !tokens[field.label]) {
      errors[field.id] = 'Verify this email address';
    } else if (field.type === 'phone' && String(value).replace(/\D/g, '').length < 7) {
      errors[field.id] = 'Enter a valid phone number';
    } else if (field.type === 'number' && Number.isNaN(Number(value))) {
      errors[field.id] = 'Enter a number';
    }
  });
  return errors;
};

/** Booking payload for the API: answers keyed by label, uploads listed as document ids. */
export const toAttendeePayload = (fields, attendee, passName) => {
  const formData = {};
  const documentIds = [];
  fields.forEach((field) => {
    const value = attendee.values[field.label];
    if (value === undefined || value === '') return;
    formData[field.label] = typeof value === 'string' ? value.trim() : value;
    if ((field.type === 'file' || field.type === 'photo') && value) documentIds.push(value);
  });
  if (passName) formData.category_pass = passName;
  const email = emailFieldOf(fields);
  return {
    formData,
    documentIds,
    uploadUserId: attendee.uploadUserId || null,
    emailToken: email ? attendee.tokens?.[email.label] || null : null,
  };
};
